import {
    Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { Add, DeleteOutlineOutlined, EditOutlined, Refresh } from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import _ from 'lodash'
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"

// The people who install locks for customers (like SpotPool's installers). An admin adds an existing user; that user then sees the
// Installer section in their Profile in the mobile app - the app recognises them by their account - with the paid orders waiting to be
// installed. They add their own coverage areas there. The server lets the super admin and admins in.
interface Installer {
    _id: string
    userId: string
    name: string
    phone: string
    country: string
    areas: string[]
    createdAt?: string
}

interface UserOption {
    _id: string
    name: string
    phone?: string
    email?: string
    profilePhotos?: any
}

const toast = (type: 'success' | 'error', message: string) => EventsEmit('modals', { action: 'toastMsg', type, message })
const userLabel = (u: UserOption) => u.name?.trim() || u.email || u.phone || ''

// Searches accounts on the server as the admin types (an installer must already have a Sheltergram account).
const UserPicker: FC<{ value: UserOption | null, disabled?: boolean, onChange: (user: UserOption | null) => void }> = ({ value, disabled, onChange }) => {
    const [options, setOptions] = useState<UserOption[]>([])
    const [loading, setLoading] = useState(false)

    // eslint-disable-next-line
    const search = useCallback(_.debounce((searchVal: string) => {
        setLoading(true)
        Apis({ module: 'Users', action: 'getUsers', searchVal, status: 'active', page: 0, pageSize: 10 })
            .then((resp: any) => setOptions(resp.users || []))
            .catch(() => setOptions([]))
            .finally(() => setLoading(false))
    }, 400), [])

    useEffect(() => () => search.cancel(), [search])

    return (
        <Autocomplete
            fullWidth
            disabled={disabled}
            value={value}
            options={options}
            loading={loading}
            filterOptions={x => x}
            isOptionEqualToValue={(option, val) => option._id === val._id}
            getOptionLabel={userLabel}
            noOptionsText="Type a name, phone number or email"
            onOpen={() => search('')}
            onInputChange={(_e, text, reason) => { if (reason === 'input') search(text) }}
            onChange={(_e, user) => onChange(user)}
            renderOption={(props, user) => (
                <li {...props} key={user._id}>
                    <Avatar src={user.profilePhotos?.small?.url} sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }} />
                    <Box>
                        <Typography sx={{ fontSize: 13 }}>{userLabel(user)}</Typography>
                        {(user.phone || user.email) && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{user.phone || user.email}</Typography>}
                    </Box>
                </li>
            )}
            renderInput={params => <TextField {...params} size="small" label="User" placeholder="Search by name, phone or email" />}
        />
    )
}

interface FormState { user: UserOption | null, name: string, country: string }
const emptyForm: FormState = { user: null, name: '', country: 'Kenya' }

const AdminLockInstallers: FC = () => {
    const [state, setState] = useState<{
        installers: Installer[], loading: boolean, refreshing: boolean,
        form: { editing: Installer | null, values: FormState, saving: boolean, error: string | null } | null,
        del: { installer: Installer, deleting: boolean, error: string | null } | null
    }>({ installers: [], loading: true, refreshing: false, form: null, del: null })
    const { installers, loading, refreshing, form, del } = state

    const fetchInstallers = ({ processVariable }: { processVariable?: string } = {}) => {
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false }
        Apis({ module: 'Locks', action: 'getInstallers' })
            .then((resp: any) => {
                if (resp.error) toast('error', resp.message)
                setState(s => resp.error ? { ...s, ...afterLoad } : { ...s, ...afterLoad, installers: resp.installers || [] })
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    useEffect(() => {
        fetchInstallers({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    // ---- add / edit ----
    const openAdd = () => setState(s => ({ ...s, form: { editing: null, values: emptyForm, saving: false, error: null } }))
    const openEdit = (installer: Installer) =>
        setState(s => ({ ...s, form: { editing: installer, values: { user: null, name: installer.name, country: installer.country }, saving: false, error: null } }))
    const closeForm = () => setState(s => (s.form?.saving ? s : { ...s, form: null }))
    const change = (patch: Partial<FormState>) => setState(s => (s.form ? { ...s, form: { ...s.form, values: { ...s.form.values, ...patch }, error: null } } : s))

    const save = () => {
        if (!form) return
        const { editing, values } = form
        setState(s => (s.form ? { ...s, form: { ...s.form, saving: true, error: null } } : s))
        Apis(editing
            ? { module: 'Locks', action: 'updateInstaller', installerId: editing._id, name: values.name, country: values.country }
            : { module: 'Locks', action: 'addInstaller', installerUserId: values.user?._id, name: values.name, country: values.country })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.form ? { ...s, form: { ...s.form, saving: false, error: resp.message } } : s))
                setState(s => ({ ...s, form: null }))
                toast('success', resp.message)
                fetchInstallers()
            })
            .catch(() => setState(s => (s.form ? { ...s, form: { ...s.form, saving: false, error: 'Could not save. Please try again.' } } : s)))
    }

    // ---- remove ----
    const closeDelete = () => setState(s => (s.del?.deleting ? s : { ...s, del: null }))
    const confirmDelete = () => {
        if (!del) return
        setState(s => (s.del ? { ...s, del: { ...s.del, deleting: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'deleteInstaller', installerId: del.installer._id })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.del ? { ...s, del: { ...s.del, deleting: false, error: resp.message } } : s))
                setState(s => ({ ...s, del: null, installers: s.installers.filter(i => i._id !== del.installer._id) }))
                toast('success', resp.message)
            })
            .catch(() => setState(s => (s.del ? { ...s, del: { ...s.del, deleting: false, error: 'Could not remove. Please try again.' } } : s)))
    }

    const canSave = Boolean(form && !form.saving && form.values.name.trim().length >= 2 && form.values.country.trim().length >= 2 && (form.editing || form.values.user))

    return (
        <Box sx={{ pr: 5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>Installers</Typography>
                        {installers.length > 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{installers.length} {installers.length === 1 ? 'installer' : 'installers'}</Typography>}
                    </Box>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 640 }}>
                        People authorised to install locks for customers. They sign in to the app as themselves; their Profile then shows the paid orders waiting to be installed.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchInstallers({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: 'none', borderRadius: 2 }}>Add installer</Button>
                </Box>
            </Box>

            {!installers.length || loading ?
                <NoData message="No installers yet. Add the first one." loading={loading} /> :
                <Paper sx={{ width: '100%', mt: 2 }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Phone</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Country</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Coverage areas</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {installers.map(installer => (
                                    <TableRow hover key={installer._id}>
                                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{installer.name}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{installer.phone}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{installer.country}</TableCell>
                                        <TableCell sx={{ fontSize: 13, maxWidth: 300 }}>
                                            {installer.areas?.length
                                                ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{installer.areas.map(area => <Chip key={area} size="small" variant="outlined" label={area} sx={{ fontSize: 11, height: 20 }} />)}</Box>
                                                : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Not set yet (the installer adds these in the app)</Typography>}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(installer)}><EditOutlined sx={{ fontSize: 19 }} /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Remove installer">
                                                <IconButton size="small" onClick={() => setState(s => ({ ...s, del: { installer, deleting: false, error: null } }))}>
                                                    <DeleteOutlineOutlined sx={{ fontSize: 19, color: 'error.main' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            }

            <Dialog open={Boolean(form)} onClose={closeForm} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>{form?.editing ? 'Edit installer' : 'Add installer'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
                        {form?.editing
                            ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{form.editing.phone}</Typography>
                            : (
                                <UserPicker
                                    value={form?.values.user || null}
                                    disabled={form?.saving}
                                    onChange={user => change({ user, ...(user && !form?.values.name.trim() ? { name: userLabel(user) } : {}) })}
                                />
                            )}
                        <TextField label="Name" size="small" fullWidth value={form?.values.name || ''} disabled={form?.saving} onChange={e => change({ name: e.target.value })} />
                        <TextField label="Country" size="small" fullWidth value={form?.values.country || ''} disabled={form?.saving} onChange={e => change({ country: e.target.value })} />
                        {form?.error && <Typography sx={{ fontSize: 13, color: 'error.main' }}>{form.error}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeForm} disabled={form?.saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={!canSave} startIcon={form?.saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ textTransform: 'none' }}>
                        {form?.editing ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(del)} onClose={closeDelete} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>Remove {del?.installer.name}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, mt: 1 }}>
                        They will stop seeing installation jobs in the app. Installations they have already marked keep their name.
                    </Typography>
                    {del?.error && <Typography sx={{ fontSize: 13, color: 'error.main', mt: 1.5 }}>{del.error}</Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDelete} disabled={del?.deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete} disabled={del?.deleting} startIcon={del?.deleting ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ textTransform: 'none' }}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminLockInstallers
